const state = {
  // Dialog visibility
  isVisible: false,

  // Content being labeled
  contentItems: null, // The item to add label to
  contentType: 'image', // 'image', 'video', 'document', etc.

  // Input state
  labelText: '',
  isLoading: false,

  // Callback functions
  onSuccess: null, // Function to call when label is successfully added
  onError: null, // Function to call when there's an error
  onCancel: null // Function to call when dialog is cancelled
};

const getters = {
  isVisible: state => state.isVisible,
  contentItems: state => state.contentItems,
  contentType: state => state.contentType,
  labelText: state => state.labelText,
  isLoading: state => state.isLoading
};

const mutations = {
  SET_VISIBLE(state, visible) {
    state.isVisible = visible;
  },

  SET_CONTENT_ITEM(state, items) {
    state.contentItems = items;
  },

  SET_CONTENT_TYPE(state, type) {
    state.contentType = type;
  },

  SET_LABEL_TEXT(state, text) {
    state.labelText = text;
  },

  SET_LOADING(state, isLoading) {
    state.isLoading = isLoading;
  },

  SET_CALLBACKS(state, { onSuccess, onError, onCancel }) {
    state.onSuccess = onSuccess;
    state.onError = onError;
    state.onCancel = onCancel;
  },

  RESET_STATE(state) {
    state.isVisible = false;
    state.contentItems = null;
    state.contentType = 'image';
    state.labelText = '';
    state.isLoading = false;
    state.onSuccess = null;
    state.onError = null;
    state.onCancel = null;
  }
};

const actions = {
  // Show add label dialog
  showAddLabelDialog(
    { commit },
    {
      items, // The items to add label to
      contentType = 'image', // Type of content ('image', 'video', 'document', etc.)
      onSuccess, // Callback when label is successfully added
      onError, // Callback when there's an error (optional)
      onCancel // Callback when user cancels (optional)
    }
  ) {
    commit('SET_CONTENT_ITEM', items);
    commit('SET_CONTENT_TYPE', contentType);
    commit('SET_CALLBACKS', { onSuccess, onError, onCancel });
    commit('SET_VISIBLE', true);
  },

  // Hide the dialog
  hideAddLabelDialog({ commit }) {
    commit('SET_VISIBLE', false);
    // Don't reset state immediately to allow for smooth closing animation
    setTimeout(() => {
      commit('RESET_STATE');
    }, 300);
  },

  // Update label text
  updateLabelText({ commit }, text) {
    commit('SET_LABEL_TEXT', text);
  },

  // Set loading state
  setLoading({ commit }, isLoading) {
    commit('SET_LOADING', isLoading);
  },

  // Add label to content
  async addLabel({ state, commit, dispatch }) {
    const trimmedLabel = state.labelText.trim();
    if (!trimmedLabel || !state.contentItems) {
      return;
    }

    commit('SET_LOADING', true);

    try {
      // Import ContentApi dynamically to avoid circular dependencies
      const { ContentApi } = await import('@/api/modules/content');

      const updatedLabelsProcesses = state.contentItems.map(item => {
        if (!item.labels) {
          item.labels = [];
        }

        return ContentApi.addLabel({
          id: item.id,
          label: trimmedLabel,
          contentType: item.type
        });
      });

      const updatedLabels = await Promise.all(updatedLabelsProcesses);

      // Call success callback with updated labels
      if (state.onSuccess) {
        const itemCount = state.contentItems.length;

        state.onSuccess({
          message: `Tag "${trimmedLabel}" added to ${itemCount} ${itemCount !== 1 ? 's' : ''}`,
          updatedLabels,
          contentItems: state.contentItems,
          newLabel: trimmedLabel
        });
      }

      dispatch('hideAddLabelDialog');
    } catch (error) {
      console.error(error);

      // Call error callback if provided
      if (state.onError) {
        state.onError({
          message: 'Failed to add tag. Please try again.',
          error: error,
          contentItem: state.contentItems
        });
      }
    } finally {
      commit('SET_LOADING', false);
    }
  },

  // Cancel adding label
  cancelAddLabel({ state, dispatch }) {
    if (state.onCancel) {
      state.onCancel();
    }
    dispatch('hideAddLabelDialog');
  }
};

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions
};
